import { useMemo, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ToggleListOption {
  key: string;
  label: ReactNode;
  meta?: ReactNode;
  checked: boolean;
}

export interface ToggleListStateInput {
  query?: string;
  searchText?: (option: ToggleListOption) => string;
  page?: number;
  pageSize?: number;
}

/** Pure client-side filter + paging pipeline so long toggle lists stay
 *  selectable without discarding checked items outside the visible page. */
export function applyToggleListState(
  options: ToggleListOption[],
  state: ToggleListStateInput
): { pageOptions: ToggleListOption[]; total: number; pageCount: number; page: number } {
  let result = options;
  const needle = (state.query ?? '').trim().toLowerCase();
  if (needle && state.searchText) {
    const searchText = state.searchText;
    result = result.filter((option) => searchText(option).toLowerCase().includes(needle));
  }
  const total = result.length;
  if (!state.pageSize) return { pageOptions: result, total, pageCount: 1, page: 0 };
  const pageCount = Math.max(1, Math.ceil(total / state.pageSize));
  const page = Math.min(Math.max(state.page ?? 0, 0), pageCount - 1);
  return {
    pageOptions: result.slice(page * state.pageSize, (page + 1) * state.pageSize),
    total,
    pageCount,
    page,
  };
}

export function ToggleList({
  title,
  help,
  options,
  empty,
  searchText,
  searchPlaceholder,
  pageSize,
  allowEmpty = false,
  busy,
  disabled,
  readOnly = false,
  saveLabel,
  onToggle,
  onSave,
}: {
  title: ReactNode;
  help?: ReactNode;
  options: ToggleListOption[];
  empty?: ReactNode;
  /** Enables client-side search across the complete option list. */
  searchText?: (option: ToggleListOption) => string;
  searchPlaceholder?: string;
  /** Enables client-side pagination without changing the complete selection. */
  pageSize?: number;
  allowEmpty?: boolean;
  busy: boolean;
  disabled?: boolean;
  /** View-only: checkboxes disabled and the save button hidden (tenant tier). */
  readOnly?: boolean;
  saveLabel: ReactNode;
  onToggle: (key: string, checked: boolean) => void;
  onSave: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const derived = useMemo(
    () => applyToggleListState(options, { query, searchText, page, pageSize }),
    [options, query, searchText, page, pageSize]
  );

  function save(event: FormEvent) {
    event.preventDefault();
    void onSave();
  }

  const hasSelection = options.some((option) => option.checked);

  return (
    <form className="panel" onSubmit={save}>
      <h2>{title}</h2>
      {help ? <p className="screen-help">{help}</p> : null}
      {searchText || pageSize ? (
        <div className="table-toolbar toggle-list-toolbar">
          {searchText ? (
            <input
              type="search"
              placeholder={searchPlaceholder ?? t('table.searchPlaceholder')}
              value={query}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(0);
              }}
            />
          ) : null}
          <span className="table-count">{t('table.rowCount', { count: derived.total })}</span>
          {pageSize ? (
            <div className="table-pager">
              <button
                type="button"
                className="ghost"
                disabled={derived.page <= 0}
                onClick={() => setPage(derived.page - 1)}
              >
                {t('table.prev')}
              </button>
              <span>
                {t('table.pageOf', { page: derived.page + 1, pageCount: derived.pageCount })}
              </span>
              <button
                type="button"
                className="ghost"
                disabled={derived.page >= derived.pageCount - 1}
                onClick={() => setPage(derived.page + 1)}
              >
                {t('table.next')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
      {derived.pageOptions.length === 0 && empty ? (
        <p className="empty-state">{empty}</p>
      ) : (
        <div className="model-list">
          {derived.pageOptions.map((option) => (
            <label key={option.key} className="check-row">
              <input
                type="checkbox"
                checked={option.checked}
                disabled={readOnly || disabled || busy}
                onChange={(event) => onToggle(option.key, event.target.checked)}
              />
              <span>
                <strong>{option.label}</strong>
                {option.meta}
              </span>
            </label>
          ))}
        </div>
      )}
      {readOnly ? null : (
        <div className="form-actions">
          <button disabled={disabled || busy || (!allowEmpty && !hasSelection)}>{saveLabel}</button>
        </div>
      )}
    </form>
  );
}
